import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  MenuItem,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { projectService } from '../services/projectService';
import { ProjectCreateDto, ProjectUpdateDto, ProjectStatus, ProjectPriority, ProjectStatusLabels, ProjectPriorityLabels } from '../types/project';
import { useAuth } from '../contexts/AuthContext';

const ProjectForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    beschreibung: '',
    beginDatum: new Date().toISOString().split('T')[0],
    endDatum: '',
    status: ProjectStatus.Geplant,
    prioritaet: ProjectPriority.Mittel,
    farbe: '#3b82f6',
    budget: '',
    istKosten: '',
    archiviert: false,
  });

  useEffect(() => {
    if (isEditMode && id) {
      loadProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadProject = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await projectService.getById(parseInt(id));
      setFormData({
        name: data.name,
        beschreibung: data.beschreibung || '',
        beginDatum: new Date(data.beginDatum).toISOString().split('T')[0],
        endDatum: data.endDatum ? new Date(data.endDatum).toISOString().split('T')[0] : '',
        status: data.status,
        prioritaet: data.prioritaet,
        farbe: data.farbe || '#3b82f6',
        budget: data.budget?.toString() || '',
        istKosten: data.istKosten?.toString() || '',
        archiviert: data.archiviert,
      });
    } catch (error) {
      console.error('Fehler beim Laden des Projekts:', error);
      alert('Fehler beim Laden des Projekts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.mandantID) {
      alert('Mandant-ID nicht gefunden');
      return;
    }

    try {
      setSaving(true);

      if (isEditMode && id) {
        const updateData: ProjectUpdateDto = {
          name: formData.name,
          beschreibung: formData.beschreibung || undefined,
          beginDatum: formData.beginDatum,
          endDatum: formData.endDatum || undefined,
          status: formData.status,
          prioritaet: formData.prioritaet,
          farbe: formData.farbe || undefined,
          budget: formData.budget ? parseFloat(formData.budget) : undefined,
          istKosten: formData.istKosten ? parseFloat(formData.istKosten) : undefined,
          archiviert: formData.archiviert,
        };
        await projectService.update(parseInt(id), updateData);
        alert('Projekt erfolgreich aktualisiert');
        navigate(`/projects/${id}`);
      } else {
        const createData: ProjectCreateDto = {
          mandantID: user.mandantID,
          name: formData.name,
          beschreibung: formData.beschreibung || undefined,
          beginDatum: formData.beginDatum,
          endDatum: formData.endDatum || undefined,
          status: formData.status,
          prioritaet: formData.prioritaet,
          farbe: formData.farbe || undefined,
          budget: formData.budget ? parseFloat(formData.budget) : undefined,
        };
        const newProject = await projectService.create(createData);
        alert('Projekt erfolgreich erstellt');
        navigate(`/projects/${newProject.projectID}`);
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern des Projekts');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(isEditMode ? `/projects/${id}` : '/projects')}
        sx={{ mb: 2 }}
      >
        Zurück
      </Button>

      <Typography variant="h4" component="h1" gutterBottom>
        {isEditMode ? 'Projekt bearbeiten' : 'Neues Projekt'}
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Projektname"
                name="name"
                value={formData.name}
                onChange={handleChange}
                inputProps={{ maxLength: 200 }}
              />
            </Grid>

            {/* Beschreibung */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Beschreibung"
                name="beschreibung"
                value={formData.beschreibung}
                onChange={handleChange}
                inputProps={{ maxLength: 2000 }}
              />
            </Grid>

            {/* Begin Datum */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Startdatum"
                name="beginDatum"
                value={formData.beginDatum}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* End Datum */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Enddatum"
                name="endDatum"
                value={formData.endDatum}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                select
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                {Object.entries(ProjectStatusLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Priorität */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                select
                label="Priorität"
                name="prioritaet"
                value={formData.prioritaet}
                onChange={handleChange}
              >
                {Object.entries(ProjectPriorityLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Farbe */}
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={2} alignItems="center">
                <input
                  type="color"
                  name="farbe"
                  value={formData.farbe}
                  onChange={handleChange}
                  style={{
                    height: '56px',
                    width: '80px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                />
                <TextField
                  fullWidth
                  label="Farbe (Hex)"
                  name="farbe"
                  value={formData.farbe}
                  onChange={handleChange}
                  inputProps={{
                    maxLength: 7,
                    pattern: '^#[0-9A-Fa-f]{6}$',
                  }}
                  placeholder="#3b82f6"
                />
              </Box>
            </Grid>

            {/* Budget */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                inputProps={{
                  step: '0.01',
                  min: '0',
                }}
              />
            </Grid>

            {/* Ist-Kosten (nur im Edit-Modus) */}
            {isEditMode && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Ist-Kosten"
                  name="istKosten"
                  value={formData.istKosten}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                  inputProps={{
                    step: '0.01',
                    min: '0',
                  }}
                />
              </Grid>
            )}

            {/* Archiviert (nur im Edit-Modus) */}
            {isEditMode && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="archiviert"
                      checked={formData.archiviert}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label="Projekt archivieren"
                />
              </Grid>
            )}
          </Grid>

          {/* Buttons */}
          <Box display="flex" justifyContent="flex-end" gap={2} mt={4} pt={3} borderTop={1} borderColor="divider">
            <Button
              variant="outlined"
              onClick={() => navigate(isEditMode ? `/projects/${id}` : '/projects')}
              disabled={saving}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Speichern...' : isEditMode ? 'Aktualisieren' : 'Erstellen'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ProjectForm;
